import type { ChangeEvent, ComponentProps, ReactNode } from 'react';
import { useState } from 'react';
import { useInstantSearch, useSearchBox } from 'react-instantsearch';
import { defineMessages, useIntl } from 'react-intl';
import { useNavigate } from 'react-router';
import styled from 'styled-components';

import { Link } from '@sorare/routing';

import { Sport } from '@sorare/core/src/__generated__/globalTypes';
import { SearchInput } from '@sorare/core/src/atoms/inputs/SearchInput';
import { Drawer } from '@sorare/core/src/atoms/layout/Drawer';
import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import {
  PopoverAnchor,
  PopoverContent,
  PopoverPortal,
  PopoverRoot,
} from '@sorare/core/src/atoms/ui/Popover';
import { InstantSearch } from '@sorare/core/src/components/search/InstantSearch';
import { MAX_RESULTS } from '@sorare/core/src/components/search/SearchBar';
import { UserSuggestion } from '@sorare/core/src/components/search/UserSuggestion';
import type { AlgoliaIndex } from '@sorare/core/src/contexts/config';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { useIsLaptop } from '@sorare/core/src/hooks/device/useIsLaptop';
import { useLatestSearchItems } from '@sorare/core/src/hooks/search/useLatestSearchItems';
import { regularMaterial } from '@sorare/core/src/style/utils';

const makeSearchBarWithChildren = (sport: Sport, indexes: AlgoliaIndex[]) =>
  function InstantSearchSearchBar({
    children,
    getOptionalFilters,
  }: {
    children?: ReactNode;
    getOptionalFilters?: ComponentProps<
      typeof InstantSearch
    >['getOptionalFilters'];
  }) {
    return (
      <InstantSearch
        indexes={indexes}
        defaultHitsPerPage={MAX_RESULTS}
        analyticsTags={['SearchBar']}
        sport={sport}
        distinct={false}
        getOptionalFilters={getOptionalFilters}
      >
        {children}
      </InstantSearch>
    );
  };

const SuggestionsContainer = styled.div`
  max-height: var(--radix-popover-content-available-height);
  overflow-y: auto;
  padding: var(--unit);
  width: var(--radix-popper-anchor-width);
  background-color: var(--c-black);
  border-radius: var(--unit);
  border: 1px solid var(--c-nd-200);
`;
const StyledDrawer = styled(Drawer)`
  width: 100%;
  padding: var(--double-unit);
  border-radius: var(--unit) var(--unit) 0 0;
  ${regularMaterial}
`;
const InnerDrawer = styled(Vertical).attrs({ gap: 2 })`
  height: 80vh;
`;
const MobileSuggestionsContainer = styled.div`
  flex: 1;
  overflow-y: auto;
`;

const messages = defineMessages({
  searchForManagers: {
    id: 'UserSearchBar.searchForManagers',
    defaultMessage: 'Search for managers...',
  },
});

type Props = {
  to: (slug: string) => string;
};

const InnerComponent = ({ to }: Props) => {
  const { query, refine } = useSearchBox();
  const { results: searchResults } = useInstantSearch();
  const { sport } = useSportContext();
  const { addItemToLatestSearch } = useLatestSearchItems(sport);
  const isLaptopAndAbove = useIsLaptop();
  const { formatMessage } = useIntl();
  const navigate = useNavigate();

  const [open, setOpen] = useState(false);
  const [highlightedSuggestionIndex, setHighlightedSuggestionIndex] = useState<
    number | null
  >(null);

  const onChange = ({ target }: ChangeEvent<HTMLInputElement>) => {
    refine(target.value);
  };

  const onFocus = () => {
    setOpen(true);
  };

  const renderInput = ({
    autoFocus,
  }: {
    autoFocus?: boolean;
  } = {}) => (
    <SearchInput
      autoFocus={autoFocus}
      withIcon
      value={query}
      onChange={onChange}
      onFocus={onFocus}
      onKeyDown={e => {
        switch (e.key) {
          case 'ArrowDown':
            setHighlightedSuggestionIndex(old => {
              switch (old) {
                case null:
                case searchResults.hits.length - 1:
                  return 0;
                default:
                  return old + 1;
              }
            });
            break;
          case 'ArrowUp':
            setHighlightedSuggestionIndex(old => {
              switch (old) {
                case null:
                case 0:
                  return searchResults.hits.length - 1;
                default:
                  return old - 1;
              }
            });
            break;
          case 'Enter':
            if (highlightedSuggestionIndex !== null) {
              setOpen(false);
              setHighlightedSuggestionIndex(null);
              navigate(
                to(searchResults.hits[highlightedSuggestionIndex].objectID)
              );
              (e.target as HTMLInputElement).blur();
            }
            break;
          default:
            break;
        }
      }}
      placeholder={formatMessage(messages.searchForManagers)}
    />
  );

  const content = searchResults.hits.map((hit, i) => (
    <Link
      key={hit.objectID}
      to={to(hit.objectID)}
      onClick={() => {
        setOpen(false);
        addItemToLatestSearch(hit);
        setHighlightedSuggestionIndex(null);
      }}
      onMouseEnter={() => setHighlightedSuggestionIndex(i)}
      onMouseLeave={() => setHighlightedSuggestionIndex(null)}
    >
      <UserSuggestion
        isHighlighted={highlightedSuggestionIndex === i}
        hit={hit}
      />
    </Link>
  ));

  if (isLaptopAndAbove) {
    return (
      <div>
        <PopoverRoot open={open} onOpenChange={setOpen} modal>
          <PopoverAnchor>{renderInput()}</PopoverAnchor>
          <PopoverPortal>
            <PopoverContent
              sideOffset={4}
              onOpenAutoFocus={e => {
                e.preventDefault();
              }}
            >
              <SuggestionsContainer>{content}</SuggestionsContainer>
            </PopoverContent>
          </PopoverPortal>
        </PopoverRoot>
      </div>
    );
  }

  return (
    <>
      {renderInput()}
      <StyledDrawer
        open={open}
        side="bottom"
        onBackdropClick={() => setOpen(false)}
      >
        <InnerDrawer gap={2}>
          {renderInput({ autoFocus: true })}
          <MobileSuggestionsContainer>{content}</MobileSuggestionsContainer>
        </InnerDrawer>
      </StyledDrawer>
    </>
  );
};

const SearchBarComponents = {
  [Sport.FOOTBALL]: makeSearchBarWithChildren(Sport.FOOTBALL, ['User']),
  [Sport.BASEBALL]: makeSearchBarWithChildren(Sport.BASEBALL, ['User']),
  [Sport.NBA]: makeSearchBarWithChildren(Sport.NBA, ['User']),
};

export const UserSearchBar = (props: Props) => {
  const { sport } = useSportContext();
  const { getOptionalFilters } = useLatestSearchItems(sport);

  const SearchBarComponent = SearchBarComponents[sport];

  return (
    <SearchBarComponent getOptionalFilters={getOptionalFilters}>
      <InnerComponent {...props} />
    </SearchBarComponent>
  );
};
